import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './Avatar.module.scss';

const getInitials = (name) => {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const Avatar = ({ src, name, size, status, className, ...rest }) => {
  // Track image-load failure so a 404'd `src` falls back to the initials
  // tile instead of leaving the browser's broken-image icon + alt text in
  // place. Reset whenever the caller swaps in a different `src`.
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  const classNames = [
    styles.avatar,
    styles[size],
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderContent = () => {
    if (src && !imageFailed) {
      return (
        <img
          className={styles.image}
          src={src}
          alt={name || 'Avatar'}
          onError={() => setImageFailed(true)}
        />
      );
    }
    if (name) {
      return <span className={styles.initials}>{getInitials(name)}</span>;
    }
    return <span className={styles.initials} />;
  };

  return (
    <div className={classNames} {...rest}>
      {renderContent()}
      {status && (
        <span className={`${styles.status} ${styles[status]}`} />
      )}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  status: PropTypes.oneOf(['online', 'offline', 'busy', 'away']),
  className: PropTypes.string,
};

Avatar.defaultProps = {
  src: '',
  name: '',
  size: 'md',
  status: null,
  className: '',
};

export default Avatar;
